import { useMediaQuery, useTheme } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  deleteAssessment,
  fetchAssessees,
  fetchAssessments,
  fetchAssessors,
} from "reduxStore/actions/assessmentActions";
import { checkRoles, getPermissionFlags, getUserScope } from "utils/permissions";
import dayjs from "dayjs";
import "dayjs/locale/en-gb";
import { fetchLevelsForOrganisation } from "reduxStore/actions/levelActions";
import { fetchDivisionForOrganisation } from "reduxStore/actions/divisionActions";
import { fetchAllLocations } from "reduxStore/actions/locationActions";
import { DeleteOutline, EditOutlined } from "@mui/icons-material";
import { setSnackbar } from "reduxStore/state";

export const useAssessments = () => {
  const dispatch = useDispatch();
  const isLargeScreen = useMediaQuery("(min-width:1000px)");
  const { palette } = useTheme();
  const backgroundAlt = palette.background.alt;
  const primaryLight = palette.primary.light;
  const primaryDark = palette.primary.dark;

  const assessments = useSelector((state) => state.assessments.data) || [];
  const totalAssessments = useSelector((state) => state.assessments.total) || 0;

  const user = useSelector((state) => state.user);
  const { organisationId, locationIds, divisionIds, levelIds } = getUserScope(user);
  const userOrg = organisationId || "";
  const userLocation = locationIds[0] || "";
  const userDivision = divisionIds[0] || "";
  const userLevel = levelIds[0] || "";
  const { showOrg, showLocation, showDivision, showLevel, showSeniorLeader } =
    getPermissionFlags(user);
  const { isAcademyImprovementLead } = checkRoles(user);

  const [selectedLocation, setSelectedLocation] = useState("");
  const [selectedDivision, setSelectedDivision] = useState("");
  const [selectedLevel, setSelectedLevel] = useState("");
  const [selectedAssessor, setSelectedAssessor] = useState("");
  const [selectedAssessee, setSelectedAssessee] = useState("");
  const [startDate, setStartDate] = useState(dayjs().subtract(12, "month"));
  const [endDate, setEndDate] = useState(dayjs());

  const locations = useSelector((state) => state.locationsAll) || [];
  const divisions = useSelector((state) => state.divisionsByLocation) || [];
  const levels = useSelector((state) => state.levelsForOrganisation) || [];
  const assessors = useSelector((state) => state.assessors) || [];
  const assesseeNames = useSelector((state) => state.assessees) || [];

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [search, setSearch] = useState("");
  //   console.log("🚀 ~ useAssessments ~ search:", search);
  const [sort, setSort] = useState("");

  const [sortModel, setSortModel] = useState([
    { field: "createdAt", sort: "desc" }, // Default sorting by createdAt
  ]);

  const [filterModel, setFilterModel] = useState({ items: [] });

  const [searchInput, setSearchInput] = useState("");

  const [open, setOpen] = useState(false);
  const [editMode, setEditMode] = useState(false); // Track if we are in edit mode
  const [currentAssessment, setCurrentAssessment] = useState(null); // Store the user being edited

  const handleEditOpen = useCallback((user) => {
    setEditMode(true);
    setCurrentAssessment(user);
    setOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [assessmentToDelete, setAssessmentToDelete] = useState(null);

  const handleDeleteOpen = useCallback((assessment) => {
    setAssessmentToDelete(assessment);
    setDeleteDialogOpen(true);
  }, []);
  const closeDeleteDialog = useCallback(() => {
    setAssessmentToDelete(null);
    setDeleteDialogOpen(false);
  }, []);

  const handleDeleteAssessmentConfirm = async () => {
    try {
      const response = await dispatch(deleteAssessment(assessmentToDelete._id));

      // Ensure response is an object
      if (!response || typeof response !== "object") {
        throw new Error("Unexpected response format");
      }

      // Determine whether the response was a success or an error
      const isSuccess = response.success && response.message;
      const message = response.message || "An unexpected error occurred";
      dispatch(
        setSnackbar({
          open: true,
          message,
          severity: isSuccess ? "success" : "error",
        })
      );
      if (isSuccess) {
        closeDeleteDialog();
        // Optionally, you can refresh the assessments list here
        dispatch(
          fetchAssessments({
            page,
            limit: pageSize,
            search,
            organisation: userOrg,
            location:
              showOrg || isAcademyImprovementLead
                ? selectedLocation
                : userLocation,
            division:
              showLevel || showSeniorLeader || isAcademyImprovementLead
                ? selectedDivision
                : userDivision,
            level:
              showDivision || showSeniorLeader || isAcademyImprovementLead
                ? selectedLevel
                : userLevel,
            assessor: selectedAssessor,
            assessee: selectedAssessee,
            startDate: startDate ? startDate.toISOString() : undefined,
            endDate: endDate ? endDate.toISOString() : undefined,
            sort:
              sortModel.length > 0 ? sortModel[0].field : "dateOfAssessment", // Default to createdAt if no sort model
            order: sortModel.length > 0 ? sortModel[0].sort : "desc", // Default to descending order
            filters: filterModel,
          })
        );
      }
    } catch (error) {
      console.error("Error while deleting assessment:", error);
      const errorMessage =
        error?.message || "Failed to delete assessment. Please try again.";
      dispatch(
        setSnackbar({
          open: true,
          message: errorMessage,
          severity: "error",
        })
      );
    }
  };

  const columns = [
    {
      field: "assessor",
      headerName: "Assessor",
      flex: 2,
      sortable: true,

      valueGetter: (params) =>
        params.row.assessor?.firstName + " " + params.row.assessor?.lastName ||
        "N/A",
    },
    {
      field: "name",
      headerName: "Assessee",
      flex: 2,
      sortable: true,
    },
    {
      field: "level",
      headerName: "Year",
      flex: 1,
      sortable: true,
      valueGetter: (params) => params.row.level?.name || "N/A",
    },
    {
      field: "division",
      headerName: "Department",
      flex: 2,
      sortable: true,
      valueGetter: (params) => params.row.division?.name || "N/A", // Get value for sorting
    },
    {
      field: "location",
      headerName: "School",
      flex: 2,
      sortable: true,
      valueGetter: (params) => params.row.location?.name || "N/A",
    },
    {
      field: "dateOfAssessment",
      headerName: "Date",
      flex: 1,
      sortable: true,
      renderCell: (params) => {
        return dayjs(params.value).format("DD/MM/YYYY");
      },
    },
    {
      field: "areaOfStrength",
      headerName: "Secure",
      flex: 4,
      sortable: false,
      filterable: false,
      valueGetter: (params) => params.row.areasOfStrength || "N/A",
    },
    {
      field: "areaOfDevelopment",
      headerName: "Developing",
      flex: 4,
      sortable: false,
      filterable: false,
      valueGetter: (params) => params.row.areasForDevelopment || "N/A",
    },
    {
      field: "edit",
      headerName: "Edit",
      flex: 1,
      filterable: false,
      renderCell: (params) =>
        user._id === params.row.assessor?._id || showLocation ? (
          <button
            onClick={() => handleEditOpen(params.row)}
            style={{ backgroundColor: "transparent", border: "none" }}
          >
            <EditOutlined sx={{ color: primaryDark }} />
          </button>
        ) : null,
    },
    showLocation && {
      field: "delete",
      headerName: "Delete",
      flex: 1,
      filterable: false,
      renderCell: (params) =>
        showLocation ? (
          <button
            onClick={() => handleDeleteOpen(params.row)}
            style={{ backgroundColor: "transparent", border: "none" }}
          >
            <DeleteOutline sx={{ color: "red" }} />
          </button>
        ) : null,
    },
  ].filter(Boolean);

  useEffect(() => {
    setPage(1);
  }, [
    selectedLocation,
    selectedDivision,
    selectedLevel,
    selectedAssessor,
    selectedAssessee,
    startDate,
    endDate,
    search,
  ]);

  useEffect(() => {
    if (userOrg) {
      dispatch(
        fetchAssessments({
          page,
          limit: pageSize,
          search,
          organisation: userOrg,
          location:
            showOrg || isAcademyImprovementLead
              ? selectedLocation
              : userLocation,
          division:
            showLevel || showSeniorLeader || isAcademyImprovementLead
              ? selectedDivision
              : userDivision,
          level:
            showDivision || showSeniorLeader || isAcademyImprovementLead
              ? selectedLevel
              : userLevel,
          assessor: selectedAssessor,
          assessee: selectedAssessee,
          startDate: startDate ? startDate.toISOString() : undefined,
          endDate: endDate ? endDate.toISOString() : undefined,
          sort: sortModel.length > 0 ? sortModel[0].field : "dateOfAssessment", // Default to createdAt if no sort model
          order: sortModel.length > 0 ? sortModel[0].sort : "desc", // Default to descending order
          filters: filterModel,
        })
      );
    }
  }, [
    page,
    pageSize,
    search,
    sort,
    sortModel,
    userOrg,
    userLocation,
    userDivision,
    userLevel,
    showOrg,
    showLocation,
    showDivision,
    showLevel,
    showSeniorLeader,
    selectedLocation,
    selectedDivision,
    selectedLevel,
    selectedAssessor,
    selectedAssessee,
    startDate,
    endDate,
    filterModel,
    dispatch,
    isAcademyImprovementLead,
  ]);

  useEffect(() => {
    dispatch(fetchAllLocations());

    dispatch(
      fetchDivisionForOrganisation({
        organisationId: userOrg,
        locationId:
          showOrg || isAcademyImprovementLead ? selectedLocation : userLocation,
      })
    );

    dispatch(
      fetchLevelsForOrganisation({
        organisationId: userOrg,
        locationId:
          showOrg || isAcademyImprovementLead ? selectedLocation : userLocation,
      })
    );

    dispatch(
      fetchAssessors({
        organisation: userOrg,
        location:
          showOrg || isAcademyImprovementLead ? selectedLocation : userLocation,
      })
    );

    dispatch(
      fetchAssessees({
        organisation: userOrg,
        location:
          showOrg || isAcademyImprovementLead ? selectedLocation : userLocation,
      })
    );
  }, [
    dispatch,
    showOrg,
    selectedLocation,
    userLocation,
    userOrg,
    isAcademyImprovementLead,
  ]);

  return {
    isLargeScreen,
    assessments,
    totalAssessments,
    showOrg,
    showDivision,
    showLevel,
    showSeniorLeader,
    isAcademyImprovementLead,
    selectedLocation,
    setSelectedLocation,
    selectedDivision,
    setSelectedDivision,
    selectedLevel,
    setSelectedLevel,
    selectedAssessor,
    setSelectedAssessor,
    selectedAssessee,
    setSelectedAssessee,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    locations,
    divisions,
    levels,
    assessors,
    assesseeNames,
    search,
    setSearch,
    searchInput,
    setSearchInput,
    page,
    setPage,
    pageSize,
    setPageSize,
    sort,
    setSort,
    sortModel,
    setSortModel,
    filterModel,
    setFilterModel,
    columns,
    backgroundAlt,
    primaryLight,
    primaryDark,
    open,
    editMode,
    currentAssessment,
    handleClose,
    setOpen,
    handleDeleteAssessmentConfirm,
    deleteDialogOpen,
    closeDeleteDialog,
  };
};
